import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, Hourglass, ScrollText } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDate, formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, Field, InlineAlert, Input, Skeleton, SourceText, Textarea, useSave } from "@ncp/ui";

interface ApprovalRecord { reference: string; referenceAr?: string | null; approvedOn: string; recordedByName: string; recordedByRole?: string | null; recordedAt: string; note?: string | null }
interface ApprovalView { approved: boolean; record?: ApprovalRecord | null; mayRecord: boolean; roles: string[]; minReferenceLength: number }

/**
 * The Department's approval of the Advocacy method (condition 1.12, finding 7.26). Until it is recorded the method is shown as proposed and is not
 * applied: instruments are recorded but not screened, and no agent drafts an assessment. A legal expert, the Director, the Assistant Under-Secretary
 * or the Under-Secretary – never an administrator – records it here with the reference and date of the Department's written approval; it is audited.
 */
export function AdvocacyMethodApproval() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const api = useApi();
  const qc = useQueryClient();
  const view = useQuery({ queryKey: ["advocacy", "method-approval"], queryFn: () => api.get<ApprovalView>("/api/advocacy/method/approval") });
  const [form, setForm] = useState({ reference: "", referenceAr: "", approvedOn: "", note: "" });
  const record = useSave({
    mutationFn: () => api.post("/api/advocacy/method/approval", { reference: form.reference.trim(), referenceAr: form.referenceAr.trim() || null, approvedOn: form.approvedOn || null, note: form.note.trim() || null }),
    expect: "record",
    saved: t("backoffice:r3Library.advocacy.approval.recorded"),
    onSuccess: () => { setForm({ reference: "", referenceAr: "", approvedOn: "", note: "" }); void qc.invalidateQueries({ queryKey: ["advocacy"] }); },
  });
  const v = view.data;
  if (view.isLoading || !v) return <Skeleton className="h-32" />;
  const r = v.record;
  const valid = form.reference.trim().length >= v.minReferenceLength && !!form.approvedOn;

  return (
    <Card className="space-y-3 p-6">
      <div className="flex flex-wrap items-center gap-2">
        <ScrollText className="size-4 text-aegold-700" aria-hidden />
        <p className="text-sm font-semibold">{t("backoffice:r3Library.advocacy.approval.title")}</p>
        {v.approved ? <Badge size="sm" variant="success"><BadgeCheck /> {t("backoffice:r3Library.advocacy.approval.approved")}</Badge> : <Badge size="sm" variant="warning"><Hourglass /> {t("backoffice:r3Library.advocacy.approval.awaiting")}</Badge>}
      </div>
      {v.approved && r ? (
        <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-[auto_minmax(0,1fr)]">
          <dt className="text-muted-foreground">{t("backoffice:r3Library.advocacy.approval.reference")}</dt><dd dir="auto"><SourceText text={ar && r.referenceAr ? r.referenceAr : r.reference} /></dd>
          <dt className="text-muted-foreground">{t("backoffice:r3Library.advocacy.approval.approvedOn")}</dt><dd>{formatDate(r.approvedOn, locale)}</dd>
          <dt className="text-muted-foreground">{t("backoffice:r3Library.advocacy.approval.recordedBy")}</dt><dd><SourceText text={r.recordedByName} />{r.recordedByRole ? ` (${t(`roles.${r.recordedByRole}`)})` : ""} · {formatDateTime(r.recordedAt, locale)}</dd>
          {r.note ? <><dt className="text-muted-foreground">{t("backoffice:r3Library.advocacy.approval.note")}</dt><dd dir="auto"><SourceText text={r.note} /></dd></> : null}
        </dl>
      ) : (
        <InlineAlert tone="warning">{t("backoffice:r3Library.advocacy.approval.notApproved")}</InlineAlert>
      )}
      {!v.approved ? (
        v.mayRecord ? (
          <div className="space-y-3 border-t border-sand-100 pt-3">
            <p className="text-xs text-muted-foreground">{t("backoffice:r3Library.advocacy.approval.recordHint")}</p>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label={t("backoffice:r3Library.advocacy.approval.reference")} hint={t("backoffice:r3Library.advocacy.approval.referenceHint")} required>
                {(id) => <Input id={id} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} dir="auto" />}
              </Field>
              <Field label={t("backoffice:r3Library.advocacy.approval.referenceAr")}>
                {(id) => <Input id={id} value={form.referenceAr} onChange={(e) => setForm({ ...form, referenceAr: e.target.value })} dir="rtl" lang="ar" />}
              </Field>
              <Field label={t("backoffice:r3Library.advocacy.approval.approvedOn")} required>
                {(id) => <Input id={id} type="date" value={form.approvedOn} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setForm({ ...form, approvedOn: e.target.value })} />}
              </Field>
              <Field label={t("backoffice:r3Library.advocacy.approval.note")}>
                {(id) => <Textarea id={id} rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} dir="auto" draft="advocacy:method:approval-note" />}
              </Field>
            </div>
            <div className="flex justify-end">
              <Button variant="gold" disabled={!valid} loading={record.isPending} onClick={() => record.mutate()}><BadgeCheck /> {t("backoffice:r3Library.advocacy.approval.record")}</Button>
            </div>
          </div>
        ) : <p className="text-xs text-muted-foreground">{t("backoffice:r3Library.advocacy.approval.whoRecords", { roles: v.roles.map((x) => t(`roles.${x}`)).join(ar ? "، " : ", ") })}</p>
      ) : null}
    </Card>
  );
}
